import { selectors } from 'modules/common';
import { useDispatch, useSelector } from 'modules/react-redux';
import { memo, useCallback, useRef } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';

import { loadMoreRequests } from '../actions';
import { RequestListItem } from '../components/RequestListItem/RequestListItem';
import { getActiveRequestId, getPagination, getRequests } from '../selectors/pageSelectors';
import type { RequestListProps } from './RequestList.types';

export const RequestList = memo<RequestListProps>((props) => {
    const { className, navigateToRequest } = props;

    const dispatch = useDispatch();
    const requests = useSelector(getRequests);
    const activeRequestId = useSelector(getActiveRequestId);
    const pagination = useSelector(getPagination);

    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const listRef = useRef<HTMLDivElement | null>(null);

    const handleEndReached = () => {
        if (pagination.hasMore) {
            dispatch(loadMoreRequests());
        }
    };

    const keyDownCallback = useCallback(
        (event: KeyboardEvent) => {
            if (!['ArrowUp', 'ArrowDown'].includes(event.key)) {
                return;
            }

            let nextIndex = requests.findIndex((request) => request.id === activeRequestId);

            if (event.key === 'ArrowUp') {
                nextIndex = Math.max(0, nextIndex - 1);
            } else if (event.key === 'ArrowDown') {
                nextIndex = Math.min(requests.length, nextIndex + 1);
            }

            virtuosoRef.current?.scrollIntoView({
                index: nextIndex,
                behavior: 'auto',
                done: () => {
                    if (requests[nextIndex]) {
                        navigateToRequest(requests[nextIndex].id, requests[nextIndex].companyId);
                    }
                },
            });
            event.preventDefault();
        },
        [activeRequestId, navigateToRequest, requests]
    );

    const handleItemClick = useCallback(
        (request: selectors.types.ExpandedRequest, selected: boolean) => () => {
            if (!selected) {
                navigateToRequest(request.id, request.companyId);
            }
        },
        [navigateToRequest]
    );

    const scrollerRef = useCallback(
        (element: HTMLDivElement) => {
            if (element) {
                if (!listRef.current) {
                    listRef.current = element;
                }

                listRef.current.addEventListener('keydown', keyDownCallback);
            } else {
                listRef.current?.removeEventListener('keydown', keyDownCallback);
            }
        },
        [keyDownCallback]
    );

    return (
        <Virtuoso
            data-qa='reql-request-list'
            ref={virtuosoRef}
            scrollerRef={scrollerRef}
            className={className}
            data={requests}
            endReached={handleEndReached}
            itemContent={(_index, request) => {
                const selected = request.id === activeRequestId;

                return (
                    <RequestListItem
                        key={request.id}
                        request={request}
                        onClick={handleItemClick(request, selected)}
                        selected={selected}
                    />
                );
            }}
        />
    );
});

RequestList.displayName = 'RequestList';
